import { apiClient } from "./client";
import type {
  CalendarEventManage,
  CalendarEventPublic,
  CreateCalendarEventDto,
  CreateCalendarEventSeriesDto,
  CreatePublicBookingRequestDto,
  PublicBookingRequestResponse,
  QueryCalendarEventsParams,
  RecurrenceScope,
  UpdateCalendarEventDto,
} from "@/types";

const buildQuery = (params: QueryCalendarEventsParams): string => {
  const search = new URLSearchParams({
    from: params.from,
    to: params.to,
  });

  if (params.roomId) {
    search.set("roomId", params.roomId);
  }

  return search.toString();
};

export const calendarService = {
  async getPublicBySlug(
    slug: string,
    params: QueryCalendarEventsParams,
  ): Promise<CalendarEventPublic[]> {
    return apiClient.get<CalendarEventPublic[]>(
      `/tenants/public/${encodeURIComponent(slug)}/calendar/events?${buildQuery(params)}`,
    );
  },

  async getMine(params: QueryCalendarEventsParams): Promise<CalendarEventManage[]> {
    return apiClient.get<CalendarEventManage[]>(
      `/tenants/me/calendar/events?${buildQuery(params)}`,
    );
  },

  async getById(id: string): Promise<CalendarEventManage> {
    return apiClient.get<CalendarEventManage>(`/tenants/me/calendar/events/${id}`);
  },

  async create(body: CreateCalendarEventDto): Promise<CalendarEventManage> {
    return apiClient.post<CalendarEventManage>("/tenants/me/calendar/events", body);
  },

  async update(id: string, body: UpdateCalendarEventDto): Promise<CalendarEventManage> {
    return apiClient.patch<CalendarEventManage>(
      `/tenants/me/calendar/events/${id}`,
      body,
    );
  },

  async remove(id: string, scope?: RecurrenceScope): Promise<CalendarEventManage | CalendarEventManage[]> {
    const query = scope ? `?scope=${scope}` : '';
    return apiClient.delete<CalendarEventManage | CalendarEventManage[]>(
      `/tenants/me/calendar/events/${id}${query}`,
    );
  },

  async createSeries(body: CreateCalendarEventSeriesDto): Promise<CalendarEventManage[]> {
    return apiClient.post<CalendarEventManage[]>("/tenants/me/calendar/event-series", body);
  },

  async createPublicBookingRequest(
    slug: string,
    body: CreatePublicBookingRequestDto,
  ): Promise<PublicBookingRequestResponse> {
    return apiClient.post<PublicBookingRequestResponse>(
      `/tenants/public/${encodeURIComponent(slug)}/calendar/booking-requests`,
      body,
    );
  },

  async updateWithScope(
    id: string,
    body: UpdateCalendarEventDto,
    scope: RecurrenceScope,
  ): Promise<CalendarEventManage | CalendarEventManage[]> {
    return apiClient.patch<CalendarEventManage | CalendarEventManage[]>(
      `/tenants/me/calendar/events/${id}?scope=${scope}`,
      body,
    );
  },
};
